<template>
  <div data-testid="cart-line" class="relative isolate flex items-start py-4 gap-3">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-500 ease-in motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <div
        v-if="spotlight"
        data-test="spotlight"
        aria-hidden="true"
        class="pointer-events-none absolute -inset-x-2 inset-y-1 -z-10 rounded-xl bg-brand-100"
      ></div>
    </Transition>

    <!-- Image / placeholder -->
    <img
      v-if="imageUrl"
      data-test="product-image"
      :src="imageUrl"
      :alt="name"
      class="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100"
      :class="{ 'cursor-pointer': canEdit }"
      @click="onEditTap"
    />
    <div
      v-else
      data-test="placeholder-tile"
      class="w-12 h-12 rounded-lg flex-shrink-0 bg-gray-100 flex items-center justify-center text-gray-400"
      :class="{ 'cursor-pointer': canEdit }"
      @click="onEditTap"
    >
      <ShoppingBagIcon class="w-5 h-5" />
    </div>

    <!-- Middle: name, variant, stepper -->
    <div class="min-w-0 flex-1" :class="{ 'opacity-60': loading }">
      <div data-test="line-details" :class="{ 'cursor-pointer': canEdit }" @click="onEditTap">
        <div class="flex items-center gap-2">
          <span
            v-if="readOnly"
            data-test="quantity-badge"
            class="inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-500 text-brand-100"
          >
            {{ quantity }}
          </span>
          <p data-testid="cart-line-name" class="text-sm font-medium text-brand-gray-800 text-left">{{ name }}</p>
          <span
            v-if="includedQuantity > 0"
            data-test="included-pill"
            class="inline-flex items-center flex-shrink-0 bg-brand-100 text-brand-800 text-xxs rounded-full px-2 py-0.5"
          >
            {{ includedLabel }}
          </span>
        </div>
        <p v-if="variantText" data-testid="cart-line-variants" class="text-xs font-light text-gray-500 text-left">
          {{ variantText }}
        </p>
        <button
          v-if="canEdit"
          type="button"
          data-testid="cart-line-edit"
          class="mt-0.5 text-xs font-medium text-brand-800 underline rounded hover:text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-800"
          :aria-label="`Edit ${name}`"
          :disabled="loading"
          @click.stop="onEditTap"
        >
          Edit
        </button>
      </div>

      <!-- Stepper (always visible when editable) -->
      <div v-if="editable && !readOnly" data-test="stepper" class="mt-2 flex items-center gap-1">
        <button
          data-test="decrement"
          :data-testid="showTrash ? 'cart-line-remove' : 'cart-line-decrement'"
          :aria-label="showTrash ? 'Remove item' : 'Decrease quantity'"
          class="w-7 h-7 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
          :disabled="loading"
          @click="decrement"
        >
          <TrashIcon v-if="showTrash" class="w-4 h-4" />
          <MinusIcon v-else class="w-4 h-4" />
        </button>
        <input
          data-test="quantity-input"
          data-testid="cart-line-quantity"
          aria-label="Quantity"
          type="text"
          inputmode="numeric"
          class="w-9 h-7 text-center border border-gray-300 rounded-lg font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-brand-800"
          :value="quantity"
          :disabled="loading"
          @change="onInput($event)"
        />
        <button
          data-test="increment"
          data-testid="cart-line-increment"
          aria-label="Increase quantity"
          class="w-7 h-7 rounded-lg border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-50"
          :class="{ 'opacity-40 cursor-not-allowed': atMax }"
          :disabled="loading || atMax"
          @click="increment"
        >
          <PlusIcon class="w-4 h-4" />
        </button>
      </div>
      <button
        v-else-if="removeReason && !readOnly"
        type="button"
        data-test="remove-line"
        data-testid="cart-line-remove"
        class="mt-2 h-7 px-3 rounded-lg inline-flex items-center text-xs font-bold text-white bg-gray-brand-600 hover:bg-gray-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-800"
        :disabled="loading"
        @click="$emit('remove')"
      >
        Remove {{ quantity }} item{{ quantity === 1 ? '' : 's' }} ({{ removeReason }})
      </button>
    </div>

    <!-- Right: price + strikethrough -->
    <div class="flex items-start gap-2 self-start" :class="{ 'opacity-60': loading }">
      <div class="text-right">
        <p class="text-sm font-medium">{{ priceFormatted }}</p>
        <p v-if="originalPriceFormatted" class="text-xs font-light text-gray-400">
          <s>{{ originalPriceFormatted }}</s>
        </p>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" data-test="item-loader" class="absolute inset-0 flex items-center justify-center">
      <svg
        class="animate-spin h-5 w-5 text-brand-800"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  </div>
</template>

<script>
import { PlusIcon, MinusIcon, TrashIcon, ShoppingBagIcon } from '@heroicons/vue/solid'
import { steppedDownQuantity, steppedUpQuantity, typedLineQuantity } from '@/utils/cartLineQuantity'

export default {
  name: 'OrderSummaryItem',
  components: { PlusIcon, MinusIcon, TrashIcon, ShoppingBagIcon },
  props: {
    imageUrl: { type: String, default: null },
    name: { type: String, required: true },
    variantText: { type: String, default: null },
    priceFormatted: { type: String, required: true },
    originalPriceFormatted: { type: String, default: null },
    quantity: { type: Number, required: true },
    editable: { type: Boolean, default: true },
    readOnly: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    includedQuantity: { type: Number, default: 0 },
    step: { type: Number, default: 1 },
    floor: { type: Number, default: 0 },
    max: { type: Number, default: null },
    removeReason: { type: String, default: null },
    allowEdit: { type: Boolean, default: false },
    // Picked out for a moment: a tint flashes in behind the row and fades away (BOT-1748).
    spotlight: { type: Boolean, default: false }
  },
  emits: ['update-quantity', 'remove', 'edit'],
  computed: {
    includedLabel() {
      return this.includedQuantity >= this.quantity ? 'Included' : `${this.includedQuantity} included`
    },
    quantityRules() {
      return { step: this.step, floor: this.floor, max: this.max }
    },
    nextUpQuantity() {
      return steppedUpQuantity(this.quantity, this.quantityRules)
    },
    nextDownQuantity() {
      return steppedDownQuantity(this.quantity, this.quantityRules)
    },
    atMax() {
      return this.nextUpQuantity == null
    },
    showTrash() {
      return this.nextDownQuantity === 0
    },
    canEdit() {
      return this.allowEdit && !this.readOnly
    }
  },
  methods: {
    increment() {
      if (this.atMax) return
      this.$emit('update-quantity', this.nextUpQuantity)
    },
    decrement() {
      if (this.showTrash) {
        this.$emit('remove')
      } else {
        this.$emit('update-quantity', this.nextDownQuantity)
      }
    },
    onInput(event) {
      const n = typedLineQuantity(parseInt(event.target.value, 10) || 0, this.quantityRules)
      if (n === 0) {
        this.$emit('remove')
        return
      }
      event.target.value = String(n ?? this.quantity)
      if (n != null) this.$emit('update-quantity', n)
    },
    onEditTap() {
      if (!this.canEdit || this.loading) return
      this.$emit('edit')
    }
  }
}
</script>
